/**
 * "What this means" and the team's notes, for one machine.
 *
 * Two different kinds of knowledge, deliberately side by side:
 *
 *   SIGNATURES are product knowledge - what the vendor's error means and what
 *   to do about it. They ship with the product and are the same everywhere.
 *
 *   NOTES are YOUR knowledge - that this client powers a server off at
 *   weekends, that someone already rang them. Nothing else in the system can
 *   know these, and today they live in somebody's head.
 *
 * Client notes are shown alongside machine notes rather than on a separate
 * page: "they close Fridays" is the answer to "why did this fail on Saturday?",
 * and an answer you have to go looking for is one nobody finds.
 */

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { useCan } from '../org';
import type { Target } from '../types';
import { MentionBox } from './MentionBox';

interface Signature {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  meaning: string;
  whatToDo: string;
}

interface Note {
  id: string;
  scope: 'target' | 'client';
  label: string;
  body: string;
  authorEmail: string;
  /** Compared against the signed-in uid, never the email - emails change. */
  authorUid?: string;
  createdAt: number | null;
  /** Set once promoted, so we offer to open it rather than promote it twice. */
  promotedTo?: string | null;
}

export function KnowledgePanel({ target, orgId }: { target: Target; orgId: string | null }) {
  const [sigs, setSigs] = useState<Signature[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [draft, setDraft] = useState('');
  const [scope, setScope] = useState<'target' | 'client'>('target');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const can = useCan();
  const myUid = auth.currentUser?.uid ?? '';

  const client = target.client?.trim() || '';

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const [ex, mine, theirs] = await Promise.all([
        httpsCallable(functions, 'explainStatus')({ orgId, text: target.statusReason, source: target.source }),
        httpsCallable(functions, 'listNotes')({ orgId, scope: 'target', key: target.id }),
        client
          ? httpsCallable(functions, 'listNotes')({ orgId, scope: 'client', key: client })
          : Promise.resolve({ data: { notes: [] } }),
      ]);
      setSigs((ex.data as { matches: Signature[] }).matches ?? []);
      setNotes([
        ...((mine.data as { notes: Note[] }).notes ?? []),
        ...((theirs.data as { notes: Note[] }).notes ?? []),
      ]);
    } catch {
      // A knowledge panel that cannot load must not take the row with it.
      setSigs([]);
    }
  }, [orgId, target.id, target.statusReason, target.source, client]);

  useEffect(() => { void load(); }, [load]);

  async function promote(n: Note) {
    const title = prompt(
      'Title for the knowledge article:',
      n.body.split('\n')[0]!.slice(0, 80)
    );
    if (!title?.trim() || !orgId) return;
    setBusy(true); setMsg(null);
    try {
      await httpsCallable(functions, 'promoteNote')({ orgId, noteId: n.id, title: title.trim() });
      setMsg('Added to the knowledge base.');
      await load();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'That did not work.');
    } finally { setBusy(false); }
  }

  async function remove(n: Note) {
    if (!orgId) return;
    const whose = n.authorUid === myUid ? 'your note' : `${n.authorEmail}'s note`;
    // A note is frequently the only written record of why a machine behaves
    // the way it does, and nothing here is recoverable afterwards.
    if (!confirm(`Delete ${whose}? This cannot be undone.`)) return;
    setBusy(true); setMsg(null);
    try {
      await httpsCallable(functions, 'deleteNote')({ orgId, id: n.id });
      setMsg('Deleted.');
      await load();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'That did not delete.');
    } finally { setBusy(false); }
  }

  async function add() {
    if (!draft.trim() || !orgId) return;
    setBusy(true); setMsg(null);
    try {
      const r = await httpsCallable(functions, 'addNote')({
        orgId,
        scope,
        key: scope === 'client' ? client : target.id,
        label: scope === 'client' ? client : target.name,
        body: draft.trim(),
      });
      const d = r.data as { mentioned?: number; missed?: string[]; summary?: string };
      const n = d.mentioned ?? 0;
      setDraft('');
      /*
       * SAY WHO WAS NOT REACHED. The whole failure Damon reported was silence:
       * the note saved, the toast said "Saved.", and the colleague named in it
       * was never told. The server now returns a sentence about both halves,
       * and it is shown at the moment the author can still fix a spelling.
       */
      setMsg('Saved' + (d.summary ? ', and ' + d.summary + '.' : '.'));
      void n;
      await load();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'That did not save.');
    } finally { setBusy(false); }
  }

  return (
    <div className="kb">
      {sigs.length > 0 && (
        <div className="kb-sigs">
          {sigs.map((s) => (
            <div className={'kb-sig kb-' + s.severity} key={s.id}>
              <div className="kb-sig-title">{s.title}</div>
              <div className="kb-sig-body">{s.meaning}</div>
              <div className="kb-sig-do"><strong>What to do:</strong> {s.whatToDo}</div>
            </div>
          ))}
        </div>
      )}

      <div className="kb-notes">
        <div className="kb-head">
          Team notes
          {notes.length > 0 && (
            <span className="tag tag-note" style={{ marginLeft: 6 }}>
              {notes.length}
            </span>
          )}
        </div>

        {notes.length === 0 && (
          <div className="kb-empty">
            Nothing written down yet. If you work out why this one behaves the way it does, this is
            where it belongs.
          </div>
        )}

        {notes.map((n) => (
          <div className="kb-note" key={n.id}>
            <div className="kb-note-meta">
              <span className={'tag ' + (n.scope === 'client' ? 'tag-warn' : '')}>
                {n.scope === 'client' ? `All of ${n.label}` : 'This machine'}
              </span>
              <span className="dim">{n.authorEmail}</span>
              {n.createdAt && <span className="dim">{new Date(n.createdAt).toLocaleDateString()}</span>}
            </div>
            <div className="kb-note-body">{n.body}</div>
            {/* A note that turns out to be general belongs in the knowledge
                base, where someone who was not here can find it. The note
                itself is KEPT - it is the record of what happened on this
                machine, and the article links back to it. */}
            <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            {/* YOUR OWN ALWAYS, ANYONE ELSE'S ONLY WITH THE CAPABILITY.
                The server decides this too - this is here so the button is
                absent rather than present and refusing, which reads as broken
                rather than as deliberate. */}
            {(Boolean(n.authorUid) && n.authorUid === myUid) || can('notes.delete.any') ? (
              <button
                className="btn-link" style={{ fontSize: 11, marginTop: 3 }} disabled={busy}
                title={n.authorUid === myUid ? 'Remove your note' : 'Remove this note'}
                onClick={() => void remove(n)}
              >
                Delete
              </button>
            ) : null}
            {n.promotedTo ? (
              <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
                Promoted to a knowledge article
              </div>
            ) : (
              <button
                className="btn-link" style={{ fontSize: 11, marginTop: 3 }} disabled={busy}
                onClick={() => void promote(n)}
              >
                Make this an article
              </button>
            )}
            </div>
          </div>
        ))}

        <div className="kb-add">
          <MentionBox
            orgId={orgId}
            value={draft}
            onChange={setDraft}
            placeholder="What does someone need to know about this? Type @ to tell a colleague."
          />
          <div className="row-form">
            {/* Scope is a radio rather than a dropdown: it changes who the note
                is about, and that is worth seeing without opening anything. */}
            <label className="check-line">
              <input type="radio" checked={scope === 'target'} onChange={() => setScope('target')} />
              <span>This machine</span>
            </label>
            {client && (
              <label className="check-line">
                <input type="radio" checked={scope === 'client'} onChange={() => setScope('client')} />
                <span>All of {client}</span>
              </label>
            )}
            <button className="btn btn-primary" disabled={busy || !draft.trim()} onClick={add}>
              {busy ? 'Saving...' : 'Add note'}
            </button>
            {msg && <span className="dim" style={{ fontSize: 11.5 }}>{msg}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
